import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import {
  BUSINESS_TYPES,
  REVENUE_RANGES,
  BUDGET_RANGES,
  TIMELINE_OPTIONS,
  SERVICES_OPTIONS,
} from "../lib/constants";
import { sendLeadEmail } from "../lib/api";

const initial = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  business_type: "",
  has_website: "",
  monthly_revenue: "",
  budget: "",
  timeline: "",
  services: [],
  biggest_challenge: "",
  project_details: "",
};

const STEPS = ["About You", "Your Business", "Project Scope", "Final Details"];

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="text-xs tracking-wider uppercase text-[#B0B0B0] font-medium">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

const inputCls =
  "field-focus w-full bg-transparent border border-[#222] focus:border-[#D4AF37] text-white placeholder:text-[#555] rounded-xl px-4 py-3 outline-none transition-all duration-200 text-sm";

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2.5 rounded-full text-sm border transition-all ${
      active
        ? "bg-[#D4AF37] text-black border-[#D4AF37] font-medium"
        : "bg-transparent text-[#B0B0B0] border-[#222] hover:border-[#D4AF37]/40 hover:text-white"
    }`}
  >
    {children}
  </button>
);

export const LeadForm = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleService = (s) =>
    setData((d) => ({
      ...d,
      services: d.services.includes(s)
        ? d.services.filter((x) => x !== s)
        : [...d.services, s],
    }));

  const canNext = () => {
    if (step === 0) {
      return data.full_name.trim() && /^\S+@\S+\.\S+$/.test(data.email) && data.phone.trim();
    }
    if (step === 1) return data.business_type;
    if (step === 2) return data.budget && data.timeline;
    return true;
  };

  const next = () => {
    if (!canNext()) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const handleSubmit = async () => {
    if (!canNext()) return;
    setSubmitting(true);
    try {
      const emailRes = await sendLeadEmail(data);
      if (emailRes.ok || emailRes.skipped) {
        toast.success("Your request has been sent. Divy will contact you shortly.");
        setDone(true);
      } else {
        toast.error("Couldn't send. Please try WhatsApp or email us directly.");
        // eslint-disable-next-line no-console
        console.error("EmailJS lead error:", emailRes.error);
      }
    } catch (e) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid="lead-form-success"
        initial={{ opacity: 0, scale: 0.94, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="glass rounded-3xl p-10 sm:p-14 text-center"
      >
        <motion.div
          initial={{ scale: 0.4, opacity: 0, rotate: -20 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.6, delay: 0.1, type: "spring", stiffness: 220, damping: 14 }}
          className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 mb-6 gold-glow"
        >
          <PartyPopper className="w-7 h-7 text-[#D4AF37]" />
        </motion.div>
        <motion.h3
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="font-display text-3xl sm:text-4xl text-white tracking-tight"
        >
          Thank you!
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="mt-4 text-[#B0B0B0] max-w-md mx-auto"
        >
          Your request has been received. Divy will contact you shortly — usually
          within a few hours.
        </motion.p>
      </motion.div>
    );
  }

  return (
    <div
      id="lead-form"
      data-testid="lead-form"
      className="glass rounded-3xl p-6 sm:p-10"
    >
      {/* Progress */}
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] font-medium">
          Step {step + 1} of {STEPS.length}
        </div>
        <div
          data-testid="lead-progress-percent"
          className="text-[10px] tracking-[0.18em] uppercase text-[#B0B0B0]"
        >
          {Math.round(((step + 1) / STEPS.length) * 100)}% complete
        </div>
      </div>
      <div className="flex items-center justify-between mb-8 gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className="relative h-1 rounded-full bg-white/10 overflow-hidden">
              <motion.div
                initial={false}
                animate={{ width: i <= step ? "100%" : "0%" }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-y-0 left-0 bg-[#D4AF37]"
              />
            </div>
            <div
              className={`text-[10px] tracking-[0.18em] uppercase mt-2 ${
                i <= step ? "text-[#D4AF37]" : "text-[#555]"
              }`}
            >
              {String(i + 1).padStart(2, "0")} · {s}
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
          {step === 0 && (
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" required>
                <input
                  data-testid="lead-input-name"
                  className={inputCls}
                  value={data.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  placeholder="Your name"
                />
              </Field>
              <Field label="Business Name">
                <input
                  data-testid="lead-input-business"
                  className={inputCls}
                  value={data.business_name}
                  onChange={(e) => update("business_name", e.target.value)}
                  placeholder="Your business"
                />
              </Field>
              <Field label="Email Address" required>
                <input
                  data-testid="lead-input-email"
                  type="email"
                  className={inputCls}
                  value={data.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Phone Number" required>
                <input
                  data-testid="lead-input-phone"
                  className={inputCls}
                  value={data.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+91 ..."
                />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-7">
              <div>
                <span className="text-xs tracking-wider uppercase text-[#B0B0B0] font-medium">
                  Business Type <span className="text-[#D4AF37]">*</span>
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {BUSINESS_TYPES.map((b) => (
                    <Chip
                      key={b}
                      testid={`lead-bt-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      active={data.business_type === b}
                      onClick={() => update("business_type", b)}
                    >
                      {b}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs tracking-wider uppercase text-[#B0B0B0] font-medium">
                  Do you currently have a website?
                </span>
                <div className="mt-3 flex gap-2">
                  {["Yes", "No"].map((o) => (
                    <Chip
                      key={o}
                      testid={`lead-has-${o.toLowerCase()}`}
                      active={data.has_website === o}
                      onClick={() => update("has_website", o)}
                    >
                      {o}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs tracking-wider uppercase text-[#B0B0B0] font-medium">
                  Monthly Revenue Range
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {REVENUE_RANGES.map((r) => (
                    <Chip
                      key={r}
                      testid={`lead-rev-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      active={data.monthly_revenue === r}
                      onClick={() => update("monthly_revenue", r)}
                    >
                      {r}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-7">
              <div>
                <span className="text-xs tracking-wider uppercase text-[#B0B0B0] font-medium">
                  Website Budget <span className="text-[#D4AF37]">*</span>
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {BUDGET_RANGES.map((r) => (
                    <Chip
                      key={r}
                      testid={`lead-budget-${r.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      active={data.budget === r}
                      onClick={() => update("budget", r)}
                    >
                      {r}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs tracking-wider uppercase text-[#B0B0B0] font-medium">
                  How soon do you need the website? <span className="text-[#D4AF37]">*</span>
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {TIMELINE_OPTIONS.map((r) => (
                    <Chip
                      key={r}
                      testid={`lead-timeline-${r.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      active={data.timeline === r}
                      onClick={() => update("timeline", r)}
                    >
                      {r}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-xs tracking-wider uppercase text-[#B0B0B0] font-medium">
                  What services do you need?
                </span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SERVICES_OPTIONS.map((s) => (
                    <Chip
                      key={s}
                      testid={`lead-svc-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      active={data.services.includes(s)}
                      onClick={() => toggleService(s)}
                    >
                      {data.services.includes(s) && (
                        <Check className="inline w-3 h-3 mr-1 -mt-0.5" />
                      )}
                      {s}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <Field label="Biggest Challenge In Your Business">
                <textarea
                  data-testid="lead-input-challenge"
                  className={`${inputCls} min-h-[100px] resize-none`}
                  value={data.biggest_challenge}
                  onChange={(e) => update("biggest_challenge", e.target.value)}
                  placeholder="What's stopping you from growing right now?"
                />
              </Field>
              <Field label="Project Details">
                <textarea
                  data-testid="lead-input-details"
                  className={`${inputCls} min-h-[120px] resize-none`}
                  value={data.project_details}
                  onChange={(e) => update("project_details", e.target.value)}
                  placeholder="Anything specific about pages, features, references you love..."
                />
              </Field>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-10 flex items-center justify-between gap-3">
        <button
          data-testid="lead-back"
          onClick={back}
          disabled={step === 0}
          className="inline-flex items-center gap-2 text-sm text-[#B0B0B0] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {step < STEPS.length - 1 ? (
          <button
            data-testid="lead-next"
            onClick={next}
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] text-black font-semibold rounded-full px-6 py-3 transition-all"
          >
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid="lead-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-gold-glow inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] disabled:opacity-60 text-black font-semibold rounded-full px-6 py-3 transition-all shadow-[0_0_30px_rgba(212,175,55,0.35)]"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
              </>
            ) : (
              <>
                Get My Free Consultation <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};
